import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePortal } from "./PortalContext";
import { MessageTagButton } from "./MessageTagButton";
import { NotificationSettings } from "@/components/NotificationSettings";

export default function PortalAccount() {
  const portal = usePortal();
  const { updateUser } = useAuth();
  const [profile, setProfile] = useState({ first_name: "", last_name: "", title: "" });
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm: "" });
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [pwNotice, setPwNotice] = useState("");
  const [pwError, setPwError] = useState("");
  useEffect(() => {
    if (portal.account) setProfile({ first_name: portal.account.profile.first_name, last_name: portal.account.profile.last_name, title: portal.account.profile.title || "" });
  }, [portal.account]);
  if (!portal.account) return <div className="cp-loading">Loading…</div>;
  const { tag_contact: contact, client, profile: current } = portal.account;

  async function saveProfile(event) {
    event.preventDefault();
    setError("");
    try {
      const response = await api.put("/client-portal/account", profile);
      setNotice("Profile saved.");
      updateUser?.({ first_name: response.data.profile.first_name, last_name: response.data.profile.last_name });
      await portal.reloadAccount();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function savePassword(event) {
    event.preventDefault();
    setPwError("");
    setPwNotice("");
    if (passwords.new_password !== passwords.confirm) { setPwError("The new passwords do not match."); return; }
    try {
      await api.post("/auth/change-password", { current_password: passwords.current_password, new_password: passwords.new_password });
      setPwNotice("Password updated.");
      setPasswords({ current_password: "", new_password: "", confirm: "" });
    } catch (requestError) {
      setPwError(formatApiError(requestError));
    }
  }

  return (
    <div className="cp-page cp-narrow" data-testid="portal-account">
      <h1 className="cp-h1">Account</h1>
      <p className="cp-lede">Your profile for {client.name}, and who to reach at TAG.</p>
      <section className="cp-card cp-card-tint" data-testid="portal-account-contact">
        <p className="cp-eyebrow">Your TAG account contact</p>
        <b className="cp-contact-name">{contact.name}</b>
        <p className="cp-muted"><a href={`mailto:${contact.email}`}>{contact.email}</a></p>
        <MessageTagButton className="cp-btn cp-btn-outline" label="Message TAG" testId="portal-account-message" />
      </section>
      <form className="cp-card cp-form" onSubmit={saveProfile} data-testid="portal-profile-form">
        <h2>Your profile</h2>
        <div className="cp-form-row">
          <label>First name<input value={profile.first_name} maxLength={80} data-testid="portal-profile-first" onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} disabled={portal.preview} required /></label>
          <label>Last name<input value={profile.last_name} maxLength={80} data-testid="portal-profile-last" onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} disabled={portal.preview} required /></label>
        </div>
        <label>Title (optional)<input value={profile.title} maxLength={120} placeholder="Director of Student Support" data-testid="portal-profile-title" onChange={(e) => setProfile({ ...profile, title: e.target.value })} disabled={portal.preview} /></label>
        <label>Email<input value={current.email} data-testid="portal-profile-email" readOnly /></label>
        {error && <p className="cp-error" data-testid="portal-profile-error">{error}</p>}
        {notice && <p className="cp-success" data-testid="portal-profile-success">{notice}</p>}
        <div className="cp-actions"><button type="submit" className="cp-btn cp-btn-primary" data-testid="portal-profile-save" disabled={portal.preview}>Save profile</button></div>
      </form>
      <form className="cp-card cp-form" onSubmit={savePassword} data-testid="portal-password-form">
        <h2>Password</h2>
        <label>Current password<input type="password" value={passwords.current_password} data-testid="portal-password-current" onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })} disabled={portal.preview} required /></label>
        <div className="cp-form-row">
          <label>New password<input type="password" value={passwords.new_password} minLength={8} data-testid="portal-password-new" onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })} disabled={portal.preview} required /></label>
          <label>Confirm new password<input type="password" value={passwords.confirm} minLength={8} data-testid="portal-password-confirm" onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} disabled={portal.preview} required /></label>
        </div>
        {pwError && <p className="cp-error" data-testid="portal-password-error">{pwError}</p>}
        {pwNotice && <p className="cp-success" data-testid="portal-password-success">{pwNotice}</p>}
        <div className="cp-actions"><button type="submit" className="cp-btn cp-btn-outline" data-testid="portal-password-save" disabled={portal.preview}>Update password</button></div>
      </form>
      <section className="cp-card" data-testid="portal-notifications">
        <h2>Notifications</h2>
        <p className="cp-muted">Get an alert on this phone when a report is ready or your TAG team changes.</p>
        <NotificationSettings variant="client" disabled={portal.preview} />
      </section>
    </div>
  );
}
